import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { AgentTypes } from '../constants/event';
import { ISOStringToEpoch } from '../utils/network';

export function selectHypothesisFrame(state: RootState) {
  return state.messages.hypothesisFrame;
}

const selectMessages = (state: RootState) => state.messages;
const selectIsAIActive = createSelector(
  selectMessages,
  (messages) => messages.isAIActive
);
const selectIsWebSocketConnected = createSelector(
  selectMessages,
  (messages) => messages.isConnected
);
const selectIsNewSession = createSelector(
  selectMessages,
  (messages) => messages.isNewSession
);
const selectRestaurantStaffIntervention = createSelector(
  selectMessages,
  (messages) => messages.restaurantStaffIntervention
);
const selectIsStaffIntervention = createSelector(
  selectMessages,
  (messages) => messages.isStaffIntervention
);
const selectIsPlaying = createSelector(
  selectMessages,
  (messages) => messages.isPlaying
);
const selectCurrentSessionId = createSelector(
  selectMessages,
  (messages) => messages.currentSessionId
);
const selectIsStaffIntervenedBeforeCarExit = createSelector(
  selectMessages,
  (messages) => messages.isStaffIntervenedBeforeCarExit
);

const selectFinalTextFrames = createSelector(
  selectMessages,
  (messages) => messages.finalTextFrames
);
const selectInProgressHypothesisTextFrames = createSelector(
  selectMessages,
  (messages) => messages.inProgressHypothesisTextFrames
);
const selectSentTextFrames = createSelector(
  selectMessages,
  (messages) => messages.sentTextFrames
);

const selectSBFinalTextFrames = createSelector(
  selectMessages,
  (messages) => messages.sbFinalTextFrames
);
const selectSBInProgressHypothesisTextFrames = createSelector(
  selectMessages,
  (messages) => messages.sbInProgressHypothesisTextFrames
);
const selectIsCarPresent = createSelector(
  selectMessages,
  (messages) => messages.isCarPresent
);

const selectEntityAnnotationsMap = createSelector(
  selectMessages,
  (messages) => messages.entityAnnotationsMap
);

// secondary_asr: recorded promoted-text occurrences (id + normalized text +
// timestamp). Consumed by MessageContainer, which binds each to the specific CX
// transcript frame nearest in time (gated on the `secondary_asr_enabled` flag
// at render).
const selectPromotedTextEvents = createSelector(
  selectMessages,
  (messages) => messages.promotedTextEvents
);

/**
 * HITL 3.0 — anchor for the Pulse "Response time" tile.
 *
 * Returns the epoch (ms) of the customer's most recent utterance IF the agent
 * has not yet replied to it, otherwise `null` (idle). Both the customer's final
 * ASR and the agent's spoken reply arrive as ORDERBOARD text frames in
 * `finalTextFrames`; they're told apart by `data.status` — a `TTS` frame is the
 * agent's reply, anything else is the customer (mirrors MessageContainer).
 *
 * The tile counts up from this anchor; the moment a TTS lands after the latest
 * customer utterance, this goes `null` and the tile resets to 0s / empty.
 */
/**
 * HITL 3.0 — the customer utterance currently awaiting an agent reply.
 *
 * Returns `{ sinceMs, text }` for the customer's most recent utterance IF the
 * agent has not yet replied to it, otherwise `null` (idle). Customer ASR and the
 * agent's spoken reply both arrive as ORDERBOARD text frames in
 * `finalTextFrames`; they're told apart by `data.status` — a `TTS` frame is the
 * agent's reply, anything else is the customer (mirrors MessageContainer).
 */
const selectAwaitingCustomerUtterance = createSelector(
  selectFinalTextFrames,
  (frames): { sinceMs: number; text: string } | null => {
    let lastCustomerMs: number | null = null;
    let lastCustomerText = '';
    let lastTtsMs: number | null = null;

    frames.forEach((frame) => {
      const ts = ISOStringToEpoch(frame?.timestamp);
      if (Number.isNaN(ts)) return;
      // Only ORDERBOARD frames matter (mirrors MessageContainer, which gates on
      // agent_type before the TTS/non-TTS split). TTS frames from other agents
      // (HITL, RESTAURANTSTAFF) also land in finalTextFrames and must NOT count as
      // "the agent replied", or they'd falsely reset the awaiting state to idle.
      if (frame?.agent_type !== AgentTypes.orderBoard) return;
      if (frame?.data?.status === 'TTS') {
        if (lastTtsMs === null || ts > lastTtsMs) lastTtsMs = ts;
      } else if (lastCustomerMs === null || ts > lastCustomerMs) {
        lastCustomerMs = ts;
        lastCustomerText = frame?.data?.payload ?? '';
      }
    });

    if (lastCustomerMs === null) return null;
    // Agent has already replied to (or after) the latest utterance → idle.
    if (lastTtsMs !== null && lastTtsMs >= lastCustomerMs) return null;
    return { sinceMs: lastCustomerMs, text: lastCustomerText };
  }
);

/**
 * Epoch (ms) anchor for the Pulse "Response time" tile — the awaiting utterance's
 * timestamp, or `null` when idle. Derived from selectAwaitingCustomerUtterance so
 * the tile's clock and the response-time metric stay in lockstep.
 */
const selectAwaitingResponseSinceMs = createSelector(
  selectAwaitingCustomerUtterance,
  (awaiting): number | null => (awaiting ? awaiting.sinceMs : null)
);

/**
 * WS-3 (HITL-1411): merge + sort transcript frames once.
 *
 * Sorts on a precomputed epoch — one `Date` parse per frame (O(n)) instead of
 * `new Date()` twice per comparison (O(n log n)) — then appends the most recent
 * in-progress hypothesis, if any, after the sorted final/sent frames. This
 * matches the previous in-hook behavior exactly; only *where/when* it runs
 * changes (memoized in the selector vs. rebuilt on every render).
 */
const buildSortedTranscript = (
  textFrames: any[],
  hypothesisFrames: any[]
): any[] => {
  const sorted = textFrames
    .map((frame) => ({ frame, epoch: ISOStringToEpoch(frame?.timestamp) }))
    .sort((a, b) => a.epoch - b.epoch)
    .map((entry) => entry.frame);

  if (hypothesisFrames.length) {
    sorted.push(hypothesisFrames[hypothesisFrames.length - 1]);
  }
  return sorted;
};

/**
 * The live transcript list: final + sent frames sorted by timestamp, with the
 * latest in-progress hypothesis appended. Memoized so it recomputes only when
 * the underlying frames change, not on every render of a consuming component.
 */
const selectTranscriptMessages = createSelector(
  selectFinalTextFrames,
  selectSentTextFrames,
  selectInProgressHypothesisTextFrames,
  (finalFrames, sentFrames, hypothesisFrames) =>
    buildSortedTranscript([...finalFrames, ...sentFrames], hypothesisFrames)
);

/**
 * The session-buffer (pre-HITL replay) transcript list, built the same way as
 * {@link selectTranscriptMessages} from the session-buffer frames.
 */
const selectSessionBufferTranscriptMessages = createSelector(
  selectSBFinalTextFrames,
  selectSBInProgressHypothesisTextFrames,
  (sbFinalFrames, sbHypothesisFrames) =>
    buildSortedTranscript([...sbFinalFrames], sbHypothesisFrames)
);

export {
  selectCurrentSessionId,
  selectFinalTextFrames,
  selectInProgressHypothesisTextFrames,
  selectIsAIActive,
  selectIsNewSession,
  selectIsPlaying,
  selectIsStaffIntervenedBeforeCarExit,
  selectIsStaffIntervention,
  selectIsWebSocketConnected,
  selectRestaurantStaffIntervention,
  selectSBFinalTextFrames,
  selectSBInProgressHypothesisTextFrames,
  selectSentTextFrames,
  selectIsCarPresent,
  selectEntityAnnotationsMap,
  selectPromotedTextEvents,
  selectAwaitingResponseSinceMs,
  selectAwaitingCustomerUtterance,
  selectTranscriptMessages,
  selectSessionBufferTranscriptMessages,
};
